import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Container, Spinner, Table } from "reactstrap";
import axios from "axios";
import { Link } from "react-router-dom";
import { getApiUrl } from "../utils/site";
import "../styles/product-details.css";

const statusColors = {
  pending: "warning",
  success: "success",
  failed: "danger",
  cancelled: "secondary",
  shipped: "info",
  delivered: "primary",
};

const paymentStatusColors = {
  pending: "warning",
  success: "success",
  failed: "danger",
  cancelled: "secondary",
  refund: "info",
};

const Profile = () => {
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(getApiUrl("/api/users/profile-with-orders"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(res.data.user);
        setOrders(Array.isArray(res.data.orders) ? res.data.orders : []);
      } catch (err) {
        setError(err.response?.data?.error || "Failed to fetch profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const stats = useMemo(() => {
    const totalOrders = orders.length;
    const activeOrders = orders.filter((order) =>
      ["pending", "shipped"].includes((order.status || "").toLowerCase())
    ).length;
    const deliveredOrders = orders.filter((order) =>
      ["delivered", "success"].includes((order.status || "").toLowerCase())
    ).length;

    return { totalOrders, activeOrders, deliveredOrders };
  }, [orders]);

  return (
    <main className="roha-page">
      <section className="roha-section">
        <Container>
          <div className="roha-section__heading roha-section__heading--row">
            <div>
              <p className="roha-eyebrow">My profile</p>
              <h1 className="section__title">Your Roha account, redesigned to feel more professional.</h1>
              <p>
                Review your personal details, order activity, and account history in a cleaner,
                more trustworthy dashboard.
              </p>
            </div>
            <Link to="/products" className="roha-button roha-button--ghost">
              Continue shopping
            </Link>
          </div>

          {loading && <div className="roha-empty-state"><Spinner color="warning" /></div>}
          {error && <Alert color="danger">{error}</Alert>}

          {!loading && !error && (
            <>
              <div className="roha-profile-stats">
                <article className="roha-profile-stat">
                  <strong>{stats.totalOrders}</strong>
                  <h3>Total orders</h3>
                  <p>All purchase records connected to your account.</p>
                </article>
                <article className="roha-profile-stat">
                  <strong>{stats.activeOrders}</strong>
                  <h3>Active orders</h3>
                  <p>Orders currently being processed or delivered.</p>
                </article>
                <article className="roha-profile-stat">
                  <strong>{stats.deliveredOrders}</strong>
                  <h3>Completed orders</h3>
                  <p>Successfully fulfilled premium jewellery purchases.</p>
                </article>
                <article className="roha-profile-stat">
                  <strong>{user?.email ? "Verified" : "-"}</strong>
                  <h3>Account status</h3>
                  <p>Profile connected and ready for faster future checkout.</p>
                </article>
              </div>

              <div className="roha-section__split">
                <article className="roha-feature-card">
                  <p className="roha-eyebrow">Account details</p>
                  <h3>{user?.full_name || "Roha customer"}</h3>
                  <div className="roha-list-card">
                    <div>
                      <strong>Email</strong>
                      <p>{user?.email || "-"}</p>
                    </div>
                    <div>
                      <strong>Phone</strong>
                      <p>{user?.phone || user?.phone_number || "-"}</p>
                    </div>
                    <div>
                      <strong>Role</strong>
                      <p>{user?.role || "Customer"}</p>
                    </div>
                  </div>
                </article>

                <article className="roha-feature-card">
                  <p className="roha-eyebrow">Account benefits</p>
                  <h3>Why this profile experience is better now</h3>
                  <div className="roha-list-card">
                    <div>
                      <strong>Clearer order visibility</strong>
                      <p>Statuses, payment progress, and purchased items are easier to scan quickly.</p>
                    </div>
                    <div>
                      <strong>More professional layout</strong>
                      <p>The account area now feels closer to a premium international ecommerce profile page.</p>
                    </div>
                    <div>
                      <strong>Higher trust</strong>
                      <p>Users get stronger confidence when account data is presented cleanly and consistently.</p>
                    </div>
                  </div>
                </article>
              </div>

              <section className="roha-profile-panel" style={{ marginTop: "1.25rem" }}>
                <div className="roha-profile-panel__header">
                  <h3>Order history</h3>
                  <p>Track order status, payment status, totals, and purchased items.</p>
                </div>

                {orders.length === 0 ? (
                  <div className="roha-empty-state">No orders found yet.</div>
                ) : (
                  <Table responsive className="align-middle">
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Status</th>
                        <th>Payment</th>
                        <th>Total</th>
                        <th>Date</th>
                        <th>Items</th>
                        <th>Track</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order.id}>
                          <td>#{order.id}</td>
                          <td>
                            <Badge color={statusColors[(order.status || "").toLowerCase()] || "secondary"}>
                              {order.status}
                            </Badge>
                          </td>
                          <td>
                            <Badge
                              color={
                                paymentStatusColors[
                                  (order.payment_status || order.payment_status_from_payments || "").toLowerCase()
                                ] || "secondary"
                              }
                            >
                              {order.payment_status || order.payment_status_from_payments || "-"}
                            </Badge>
                          </td>
                          <td>ETB {order.total}</td>
                          <td>{order.created_at ? new Date(order.created_at).toLocaleString() : "-"}</td>
                          <td>
                            <ul className="mb-0 ps-3">
                              {(order.items || []).map((item, i) => (
                                <li key={i}>
                                  {item.product_name || item.product_id} x {item.quantity} @ ETB {item.price}
                                </li>
                              ))}
                            </ul>
                          </td>
                          <td>
                            <Link to={`/orders/${order.hash_id || order.id}`} className="roha-button roha-button--ghost">
                              Track order
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </section>
            </>
          )}
        </Container>
      </section>
    </main>
  );
};

export default Profile;
